<template>
  <div
    ref="overflowContainer"
    :class="ns.b()"
    :style="{ flexWrap: props.maxCount ? 'wrap' : 'nowrap' }"
  >
    <template v-if="Array.isArray(props.items) && props.items.length">
      <div v-for="(item, index) in transItems" :key="index" ref="itemRefs">
        <!-- default的插槽 -->
        <slot name="default" :item="item" />
      </div>
    </template>
    <slot v-else ref="itemRefs" />

    <!-- counter 插槽 -->
    <div v-if="$slots.counter" ref="counter">
      <slot name="counter" :count="count" />
    </div>

    <!-- suffix 的插槽 -->
    <div v-if="$slots.suffix" ref="suffixRef">
      <slot name="suffix" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, toRaw, useSlots, watch } from 'vue'
import { useNamespace } from '@lit-element/hooks'
import { overflowProps } from './overflow'

const ns = useNamespace('overflow')
const props = defineProps(overflowProps)
const slots = useSlots() // 使用 useSlots 获取插槽
defineOptions({
  name: 'LtOverflow',
})
const transItems = ref<string[]>([])
const count = ref(0) //剩下的个数
const overflowContainer = ref<HTMLElement | null>(null)
const itemRefs = ref<HTMLElement[]>([])
const counter = ref<HTMLElement | null>(null)
const suffixRef = ref<HTMLElement | null>(null)

onMounted(() => {
  transItems.value = (props.items as string[]) || []
  updateItems()
})

watch(
  () => [props.items, props.maxCount],
  (newData) => {
    const data = toRaw(newData)
    transItems.value = Array.isArray(data[0]) ? (data[0] as string[]) : []
    updateItems()
  }
)

watch(
  () => slots,
  (newData) => {
    transItems.value = (props.items as string[]) || []
    updateItems()
  },
  { deep: true }
)

const updateItems = async () => {
  await nextTick()
  nextTick(() => {
    // 容器的宽度
    const containerWidth = overflowContainer.value?.offsetWidth || 0
    // 计算 suffix
    const suffixRefWidth = suffixRef.value?.offsetWidth || 0

    // 如果counterWidth = 0或者空，表示没有传入counter插槽
    const counterWidth = counter.value?.offsetWidth || 0
    // 计算去除suffix后剩余宽度，方便计算还剩下的个数
    const leftoverWidth = containerWidth - suffixRefWidth - counterWidth

    if (!props.items || !Array.isArray(props.items)) {
      const allChilden = Array.prototype.slice.call(
        overflowContainer.value?.children || []
      )
      if (counterWidth) {
        allChilden.pop()
      }
      itemRefs.value = allChilden
    } else {
      const allChilden = Array.prototype.slice.call(
        overflowContainer.value?.children || []
      )
      if (counterWidth) {
        allChilden.pop()
      }
      itemRefs.value = allChilden
    }

    // 计算count
    let totalWidth = 0,
      transCount = 0,
      overflowStatus = false
    for (const item of itemRefs.value) {
      totalWidth += item.offsetWidth + 4
      if (totalWidth <= leftoverWidth) {
        transCount++
      }
    }
    // 剩下的个数
    let spliceIndex = props.maxCount ? props.maxCount : transCount
    if (Array.isArray(props.items) && props.items.length) {
      count.value = props.maxCount
        ? props.items.length - props.maxCount
        : props.items.length - transCount
      // 深拷贝 防止splice影响原数据
      const transItem = JSON.parse(JSON.stringify(props.items))

      if (props.maxCount >= props.items.length) {
        spliceIndex = props.items.length
      }
      if (spliceIndex) overflowStatus = true
      transItems.value = transItem.splice(0, spliceIndex)
    } else {
      count.value = props.maxCount
        ? itemRefs.value.length - props.maxCount
        : itemRefs.value.length - transCount
      // 如果设置了最大个数 且小于计算得到的剩余个数，就截取就取最大个数，没有就正常逻辑截取
      const removeMiddleChildren = () => {
        if (overflowContainer.value) {
          // 确定要删除的子元素的范围，例如删除索引为 1 和 2 的子元素
          const startIndex = spliceIndex // 开始索引（包含）
          let endIndex = itemRefs.value.length // 结束索引（不包含）
          if (suffixRef.value) {
            endIndex--
          }
          // 获取所有子元素
          const children = overflowContainer.value.children

          // 从后往前删除，以避免索引变化影响删除操作
          for (let i = endIndex - 1; i >= startIndex; i--) {
            overflowContainer.value.removeChild(children[i])
          }
        }
      }

      removeMiddleChildren()
    }

    emit('toggle', overflowStatus)
  })
}
const emit = defineEmits(['toggle'])
</script>
